import type { ReactNode } from 'react';
import { profileBreakdownOf, type IcpItem, type IcpRanked } from '@nest-recruit/shared';
import { ProfileBreakdownView } from '../ProfileBreakdownView';
import styles from '../../../icp/components/icp.module.css';

// ADR 0062, RNK-02, mockup M2 "Why they're Nth": the profile's reasons for one person come first in the sheet — the match, the
// must-have callout and one row per line. Unknown is never a no: a line the person left open says so, in words.

const ORDINAL_SUFFIXES: Readonly<Record<number, string>> = { 1: 'st', 2: 'nd', 3: 'rd' };
const TEEN_FROM = 11;
const TEEN_TO = 13;

export function ordinal(place: number): string {
  const teen = place % 100;
  if (teen >= TEEN_FROM && teen <= TEEN_TO) return `${place}th`;
  return `${place}${ORDINAL_SUFFIXES[place % 10] ?? 'th'}`;
}

interface IcpWhyProps {
  readonly items: readonly IcpItem[];
  readonly ranked: IcpRanked | undefined;
  /** The knockout penalty already taken off the match (§L8). */
  readonly penalty: number;
  /** Their place in the profile's order in this tab; null when they are in no tab. */
  readonly place: number | null;
}

export function IcpWhy({ items, ranked, penalty, place }: IcpWhyProps): ReactNode {
  if (!ranked) return null;
  const breakdown = profileBreakdownOf(items, ranked.values, ranked, ranked.state, penalty);
  return (
    <section className={styles.why} aria-label="Why they are placed here for this profile">
      <h3 className={styles.whyTitle}>{place === null ? 'Where they are for this profile' : `Why they’re ${ordinal(place)} for this profile`}</h3>
      <ProfileBreakdownView breakdown={breakdown} />
      <p className={styles.whyNote}>The order follows the ratings: a line rated 5 must be shown to come first. Match, fit and covers are worked out from Jev’s stored reading.</p>
    </section>
  );
}
